/**
 * Interactive Map functionality
 * Canvas-based implementation for reliable cross-browser rendering
 */

// Configuration constants
const CONFIG = {
  MAX_SCALE: 5,
  ZOOM_SENSITIVITY: {
    TRACKPAD: 0.05,
    MOUSE: 0.1,
  },
  ZOOM_STEP: 1.2,
  TOOLTIP_DIMENSIONS: {
    WIDTH: 320,
    HEIGHT: 400,
  },
  // Default color mapping for different availability statuses
  DEFAULT_COLOR_MAP: {
    beschikbaar: 'rgba(34, 197, 94, 0.7)', // green
    verkocht: 'rgba(239, 68, 68, 0.6)', // red
    in_optie: 'rgba(249, 115, 22, 0.6)', // orange
    niet_beschikbaar: 'rgb(255, 255, 255, 0.38)', // gray
  },
  HOVER_OPACITY_INCREASE: 0.2,
};

/**
 * Interactive Map Class
 * Manages all map functionality using Canvas for rendering
 */
class InteractiveMap {
  constructor(blockId, containerId, mapData, propertyData) {
    this.blockId = blockId;
    this.containerId = containerId;
    this.mapData = mapData;
    this.propertyData = propertyData || {};
    this.colorMap = CONFIG.DEFAULT_COLOR_MAP;

    // DOM elements
    this.container = document.getElementById(containerId);
    this.canvas = null;
    this.ctx = null;
    this.mapImage = null;
    this.tooltip = null;
    this.mobileOverlay = null;
    this.mapContainer = null;
    this.zoomInBtn = null;
    this.zoomOutBtn = null;
    this.fullscreenBtn = null;

    // Tooltip child elements (cached for reuse)
    this.tooltipImage = null;
    this.tooltipTitle = null;
    this.tooltipLocation = null;
    this.tooltipDescription = null;
    this.tooltipPrice = null;
    this.tooltipButton = null;

    // Mobile overlay child elements
    this.mobileOverlayImage = null;
    this.mobileOverlayTitle = null;
    this.mobileOverlayLocation = null;
    this.mobileOverlayDescription = null;
    this.mobileOverlayPrice = null;
    this.mobileOverlayButton = null;
    this.mobileOverlayClose = null;

    // Polygon data for hit detection
    this.polygons = [];
    this.hoveredPolygon = null;

    // State management
    this.state = {
      scale: 1,
      translateX: 0,
      translateY: 0,
      isDragging: false,
      dragStart: { x: 0, y: 0 },
      containerWidth: 0,
      containerHeight: 0,
      imageWidth: 0,
      imageHeight: 0,
      minScale: 1,
      maxScale: CONFIG.MAX_SCALE,
      isHoveringTooltip: false,
      isFullscreen: false,
      needsRedraw: false,
      overlayJustShown: false, // Prevent immediate hiding after showing overlay
    };

    // Touch and scroll handling
    this.initialTouchY = undefined;
    this.initialTouchX = undefined;
    this.touchStarted = false;
    this.lastTouchDistance = null;
    this.touchedPolygon = null; // Track polygon at touch start

    // Animation frame request ID
    this.rafId = null;

    // Image loading state
    this.currentImageLoad = null; // Track current image preload operation
    this.requestedImageUrl = null; // Track the URL we're currently trying to load
    this.currentMobileImageLoad = null; // Track mobile overlay image preload
    this.requestedMobileImageUrl = null; // Track mobile overlay URL

    this.init();
  }

  /**
   * Initialize the interactive map
   */
  init() {
    console.log('Interactive map initialized (Canvas)', this.container, this.blockId);
    if (!this.container) return;

    this.getDOMElements();
    if (!this.validateElements()) return;

    this.setupCanvas();
    this.setupEventListeners();
    this.loadMapImage();
    this.startRenderLoop();
  }

  /**
   * Get all required DOM elements
   */
  getDOMElements() {
    this.canvas = this.container.querySelector(`#map-canvas-${this.blockId}`);
    this.tooltip = document.getElementById('map-tooltip');
    this.mobileOverlay = document.getElementById('map-mobile-overlay');
    this.mapContainer = this.container.querySelector('.interactive-map');
    this.zoomInBtn = this.container.querySelector(`#zoom-in-${this.blockId}`);
    this.zoomOutBtn = this.container.querySelector(`#zoom-out-${this.blockId}`);
    this.fullscreenBtn = this.container.querySelector(`#fullscreen-${this.blockId}`);

    // Cache tooltip child elements
    if (this.tooltip) {
      this.tooltipImage = this.tooltip.querySelector('#tooltip-image');
      this.tooltipTitle = this.tooltip.querySelector('#tooltip-title');
      this.tooltipLocation = this.tooltip.querySelector('#tooltip-location');
      this.tooltipDescription = this.tooltip.querySelector('#tooltip-description');
      this.tooltipPrice = this.tooltip.querySelector('#tooltip-price');
      this.tooltipButton = this.tooltip.querySelector('#tooltip-button');
    }

    // Cache mobile overlay child elements
    if (this.mobileOverlay) {
      this.mobileOverlayImage = this.mobileOverlay.querySelector('#mobile-overlay-image');
      this.mobileOverlayTitle = this.mobileOverlay.querySelector('#mobile-overlay-title');
      this.mobileOverlayLocation = this.mobileOverlay.querySelector('#mobile-overlay-location');
      this.mobileOverlayDescription = this.mobileOverlay.querySelector(
        '#mobile-overlay-description'
      );
      this.mobileOverlayPrice = this.mobileOverlay.querySelector('#mobile-overlay-price');
      this.mobileOverlayButton = this.mobileOverlay.querySelector('#mobile-overlay-button');
      this.mobileOverlayClose = this.mobileOverlay.querySelector('#mobile-overlay-close');
    }
  }

  /**
   * Validate that all required elements exist
   */
  validateElements() {
    return !!(this.canvas && this.mapContainer);
  }

  /**
   * Check if current device is mobile
   */
  isMobileDevice() {
    return window.innerWidth <= 768;
  }

  /**
   * Setup canvas element
   */
  setupCanvas() {
    this.ctx = this.canvas.getContext('2d', { alpha: false });
    this.updateCanvasSize();
  }

  /**
   * Update canvas size to match container
   */
  updateCanvasSize() {
    const rect = this.mapContainer.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    // Set canvas size accounting for device pixel ratio
    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;

    // Set display size
    this.canvas.style.width = `${rect.width}px`;
    this.canvas.style.height = `${rect.height}px`;

    // Scale context for high DPI displays
    this.ctx.scale(dpr, dpr);

    this.state.containerWidth = rect.width;
    this.state.containerHeight = rect.height;

    this.state.needsRedraw = true;
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Window resize with debouncing
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.updateCanvasSize();
        this.updateDimensions();
        this.updateZoomButtons();
        this.state.needsRedraw = true;
      }, 100);
    });

    // Fullscreen change events
    document.addEventListener('fullscreenchange', () => {
      this.state.isFullscreen = !!document.fullscreenElement;
      this.updateFullscreenButton();
      this.handleFullscreenChange();
    });

    // Setup interaction events
    this.setupZoomControls();
    this.setupCanvasInteractions();
    this.setupTooltipEvents();
  }

  /**
   * Setup canvas interaction events
   */
  setupCanvasInteractions() {
    // Mouse events
    this.canvas.addEventListener('mousedown', (e) => this.handleMouseDown(e));
    this.canvas.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    this.canvas.addEventListener('mouseup', (e) => this.handleMouseUp(e));
    this.canvas.addEventListener('mouseleave', (e) => this.handleMouseLeave(e));

    // Touch events
    this.canvas.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: false });
    this.canvas.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: false });
    this.canvas.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: false });
    this.canvas.addEventListener('touchcancel', (e) => this.handleTouchEnd(e), { passive: false });

    // Wheel zoom (trackpad pinch)
    this.canvas.addEventListener('wheel', (e) => this.handleWheel(e), { passive: false });
  }

  /**
   * Load map image
   */
  loadMapImage() {
    // Get image URL from map data or ACF field
    const imageUrl = this.mapContainer.dataset.mapImage || this.getImageUrlFromBlock();

    if (!imageUrl) {
      console.error('No map image URL found');
      return;
    }

    this.mapImage = new Image();
    this.mapImage.crossOrigin = 'anonymous';
    this.mapImage.onload = () => {
      this.updateDimensions();
      this.buildPolygonData();
      this.state.needsRedraw = true;
    };
    this.mapImage.onerror = (e) => {
      console.error('Failed to load map image:', e);
    };
    this.mapImage.src = imageUrl;
  }

  /**
   * Get image URL from block (fallback method)
   */
  getImageUrlFromBlock() {
    // This will be set from PHP
    return this.canvas.dataset.imageUrl || '';
  }

  /**
   * Update dimensions and initial scale
   */
  updateDimensions() {
    if (!this.mapImage || !this.mapImage.complete) return;

    this.state.imageWidth = this.mapImage.naturalWidth;
    this.state.imageHeight = this.mapImage.naturalHeight;

    // Calculate the scale needed to fill the container (cover behavior)
    const scaleX = this.state.containerWidth / this.state.imageWidth;
    const scaleY = this.state.containerHeight / this.state.imageHeight;
    this.state.minScale = Math.max(scaleX, scaleY);

    // Check if we're on mobile
    const isMobile = window.innerWidth <= 768;

    // Set initial scale
    if (isMobile) {
      this.state.scale = this.state.minScale * 1.3; // Start slightly zoomed in on mobile
      this.state.maxScale = CONFIG.MAX_SCALE; // Allow pinch zoom on mobile
    } else {
      this.state.scale = this.state.minScale;
      this.state.maxScale = CONFIG.MAX_SCALE;
    }

    // Center the image
    this.centerImage();
    this.updateZoomButtons();
  }

  /**
   * Center the image in the container
   */
  centerImage() {
    this.state.translateX =
      (this.state.containerWidth - this.state.imageWidth * this.state.scale) / 2;
    this.state.translateY =
      (this.state.containerHeight - this.state.imageHeight * this.state.scale) / 2;
  }

  /**
   * Build polygon data for rendering and hit detection
   */
  buildPolygonData() {
    if (!this.mapData || !this.mapData.shapes) return;

    this.polygons = [];

    this.mapData.shapes.forEach((shape) => {
      if (!shape.points_percent || !shape.meta || !shape.meta.objectCode) return;

      const objectCode = shape.meta.objectCode;
      const propertyInfo = this.propertyData[objectCode];
      const status = propertyInfo ? propertyInfo.status : 'niet_beschikbaar';

      if (!propertyInfo) return;

      // Convert percentage points to normalized coordinates (0-1)
      const points = [];
      for (let i = 0; i < shape.points_percent.length; i += 2) {
        points.push({
          x: shape.points_percent[i] / 100,
          y: shape.points_percent[i + 1] / 100,
        });
      }

      this.polygons.push({
        points: points,
        objectCode: objectCode,
        status: status,
        propertyInfo: propertyInfo,
        baseColor: this.colorMap[status] || this.colorMap['niet_beschikbaar'],
        hoverColor: this.adjustOpacity(
          this.colorMap[status] || this.colorMap['niet_beschikbaar'],
          0.9
        ),
      });
    });
  }

  /**
   * Main render loop
   */
  startRenderLoop() {
    const render = () => {
      if (this.state.needsRedraw) {
        this.render();
        this.state.needsRedraw = false;
      }
      this.rafId = requestAnimationFrame(render);
    };
    render();
  }

  /**
   * Render everything to canvas
   */
  render() {
    if (!this.ctx || !this.mapImage || !this.mapImage.complete) return;

    // Clear canvas
    this.ctx.clearRect(0, 0, this.state.containerWidth, this.state.containerHeight);

    // Save context state
    this.ctx.save();

    // Apply transform
    this.ctx.translate(this.state.translateX, this.state.translateY);
    this.ctx.scale(this.state.scale, this.state.scale);

    // Draw map image
    this.ctx.drawImage(this.mapImage, 0, 0, this.state.imageWidth, this.state.imageHeight);

    // Draw polygons
    this.drawPolygons();

    // Restore context state
    this.ctx.restore();
  }

  /**
   * Draw all polygons
   */
  drawPolygons() {
    this.polygons.forEach((polygon) => {
      const isHovered = polygon === this.hoveredPolygon;
      const color = isHovered ? polygon.hoverColor : polygon.baseColor;

      this.ctx.fillStyle = color;
      this.ctx.strokeStyle = color;
      this.ctx.lineWidth = 2 / this.state.scale; // Keep stroke width consistent regardless of zoom

      this.ctx.beginPath();
      polygon.points.forEach((point, index) => {
        const x = point.x * this.state.imageWidth;
        const y = point.y * this.state.imageHeight;
        if (index === 0) {
          this.ctx.moveTo(x, y);
        } else {
          this.ctx.lineTo(x, y);
        }
      });
      this.ctx.closePath();
      this.ctx.fill();
      this.ctx.stroke();
    });
  }

  /**
   * Handle mouse down
   */
  handleMouseDown(e) {
    if (this.state.isHoveringTooltip) return;

    // Check if clicking on a polygon - on desktop, just keep tooltip visible (already shown on hover)
    // Navigation happens via the button in the tooltip
    const clickedPolygon = this.getPolygonAtPosition(e.clientX, e.clientY);
    if (clickedPolygon) {
      // Don't navigate on click - tooltip button handles that
      return;
    }

    // Start dragging
    this.state.isDragging = true;
    this.state.dragStart.x = e.clientX - this.state.translateX;
    this.state.dragStart.y = e.clientY - this.state.translateY;
    this.canvas.style.cursor = 'grabbing';
  }

  /**
   * Handle mouse move
   */
  handleMouseMove(e) {
    if (this.state.isDragging) {
      this.drag(e.clientX, e.clientY);
    } else {
      this.checkHover(e.clientX, e.clientY);
    }
  }

  /**
   * Handle mouse up
   */
  handleMouseUp(e) {
    this.endDrag();
  }

  /**
   * Handle mouse leave
   */
  handleMouseLeave(e) {
    this.endDrag();
    this.clearHover();
  }

  /**
   * Drag the map
   */
  drag(clientX, clientY) {
    const newTranslateX = clientX - this.state.dragStart.x;
    const newTranslateY = clientY - this.state.dragStart.y;

    // Calculate boundaries
    const boundaries = this.calculateBoundaries();

    // Constrain translation within boundaries
    this.state.translateX = Math.max(boundaries.minX, Math.min(boundaries.maxX, newTranslateX));
    this.state.translateY = Math.max(boundaries.minY, Math.min(boundaries.maxY, newTranslateY));

    this.state.needsRedraw = true;
  }

  /**
   * End dragging
   */
  endDrag() {
    this.state.isDragging = false;
    this.canvas.style.cursor = 'grab';
  }

  /**
   * Check for hover over polygons (desktop only)
   */
  checkHover(clientX, clientY) {
    // Skip hover functionality on mobile
    if (this.isMobileDevice()) {
      return;
    }

    const polygon = this.getPolygonAtPosition(clientX, clientY);

    if (polygon !== this.hoveredPolygon) {
      // Hover state changed
      const wasHovering = !!this.hoveredPolygon;
      this.hoveredPolygon = polygon;

      if (polygon) {
        this.canvas.style.cursor = 'pointer';
        this.showTooltip(clientX, clientY, polygon);
      } else {
        this.canvas.style.cursor = 'grab';
        if (wasHovering) {
          this.scheduleHideTooltip();
        }
      }

      this.state.needsRedraw = true;
    } else if (polygon) {
      // Still hovering over same polygon, update tooltip position
      this.updateTooltipPosition(clientX, clientY, polygon);
    }
  }

  /**
   * Clear hover state
   */
  clearHover() {
    if (this.hoveredPolygon) {
      this.hoveredPolygon = null;
      this.scheduleHideTooltip();
      this.state.needsRedraw = true;
    }
  }

  /**
   * Get polygon at screen position using hit detection
   */
  getPolygonAtPosition(clientX, clientY) {
    const rect = this.canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    // Convert screen coordinates to image coordinates
    const imageX = (x - this.state.translateX) / this.state.scale;
    const imageY = (y - this.state.translateY) / this.state.scale;

    // Check each polygon (in reverse order so topmost is checked first)
    for (let i = this.polygons.length - 1; i >= 0; i--) {
      const polygon = this.polygons[i];
      if (this.isPointInPolygon(imageX, imageY, polygon.points)) {
        return polygon;
      }
    }

    return null;
  }

  /**
   * Point-in-polygon test using ray casting algorithm
   */
  isPointInPolygon(x, y, points) {
    let inside = false;
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
      const xi = points[i].x * this.state.imageWidth;
      const yi = points[i].y * this.state.imageHeight;
      const xj = points[j].x * this.state.imageWidth;
      const yj = points[j].y * this.state.imageHeight;

      const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  }

  /**
   * Handle polygon click
   */
  handlePolygonClick(polygon) {
    // Trigger custom event
    const event = new CustomEvent('mapPropertyClick', {
      detail: {
        objectCode: polygon.objectCode,
        status: polygon.status,
        propertyInfo: polygon.propertyInfo,
      },
    });
    document.dispatchEvent(event);

    // Navigate to property page if available
    if (polygon.propertyInfo && polygon.propertyInfo.permalink) {
      window.location.href = polygon.propertyInfo.permalink;
    }
  }

  /**
   * Calculate drag boundaries
   */
  calculateBoundaries() {
    const scaledImageWidth = this.state.imageWidth * this.state.scale;
    const scaledImageHeight = this.state.imageHeight * this.state.scale;

    return {
      minX:
        scaledImageWidth > this.state.containerWidth
          ? this.state.containerWidth - scaledImageWidth
          : 0,
      maxX: 0,
      minY:
        scaledImageHeight > this.state.containerHeight
          ? this.state.containerHeight - scaledImageHeight
          : 0,
      maxY: 0,
    };
  }

  /**
   * Touch event handlers
   */
  handleTouchStart(e) {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      this.initialTouchX = touch.clientX;
      this.initialTouchY = touch.clientY;
      this.touchStarted = true;

      // Check if touching a polygon and save it
      const polygon = this.getPolygonAtPosition(touch.clientX, touch.clientY);
      this.touchedPolygon = polygon; // Save the polygon we touched

      if (!polygon && !this.state.isHoveringTooltip) {
        // Only prevent default if not touching polygon or tooltip
        e.preventDefault();
      }
    } else if (e.touches.length === 2) {
      e.preventDefault();
      const distance = this.getTouchDistance(e.touches[0], e.touches[1]);
      this.lastTouchDistance = distance;
    }
  }

  handleTouchMove(e) {
    if (e.touches.length === 1) {
      const touch = e.touches[0];

      if (this.state.isDragging) {
        e.preventDefault();
        this.drag(touch.clientX, touch.clientY);
        return;
      }

      if (
        this.touchStarted &&
        this.initialTouchX !== undefined &&
        this.initialTouchY !== undefined
      ) {
        const deltaX = Math.abs(touch.clientX - this.initialTouchX);
        const deltaY = Math.abs(touch.clientY - this.initialTouchY);

        if (deltaX > 5 || deltaY > 5) {
          const polygon = this.getPolygonAtPosition(touch.clientX, touch.clientY);
          if (!polygon && !this.state.isHoveringTooltip) {
            e.preventDefault();
            this.state.isDragging = true;
            this.touchedPolygon = null; // Clear touched polygon since we're dragging
            this.state.dragStart.x = touch.clientX - this.state.translateX;
            this.state.dragStart.y = touch.clientY - this.state.translateY;
            this.drag(touch.clientX, touch.clientY);
          }
        }
      }
    } else if (e.touches.length === 2) {
      this.handlePinchZoom(e);
    }
  }

  handleTouchEnd(e) {
    // Check if this was a tap on a polygon (not a drag)
    if (e.changedTouches.length === 1) {
      const wasDragging = this.state.isDragging;
      const touch = e.changedTouches[0];

      // If we didn't drag and we touched a polygon at the start
      if (!wasDragging && this.touchedPolygon) {
        // Verify we're still over the same polygon
        const currentPolygon = this.getPolygonAtPosition(touch.clientX, touch.clientY);

        // If still over a polygon, treat as a tap
        if (currentPolygon) {
          if (this.isMobileDevice()) {
            // On mobile, show overlay
            // Prevent event propagation to avoid triggering backdrop click
            e.preventDefault();
            e.stopPropagation();
            this.showMobileOverlay(currentPolygon);
          } else {
            // On desktop with touch, could be tablet - show tooltip or navigate
            this.handlePolygonClick(currentPolygon);
          }
        }
      }
    }

    // Reset all touch state
    this.state.isDragging = false;
    this.touchStarted = false;
    this.initialTouchX = undefined;
    this.initialTouchY = undefined;
    this.lastTouchDistance = null;
    this.touchedPolygon = null;
  }

  /**
   * Get distance between two touch points
   */
  getTouchDistance(touch1, touch2) {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Handle pinch zoom
   */
  handlePinchZoom(e) {
    e.preventDefault();
    const distance = this.getTouchDistance(e.touches[0], e.touches[1]);

    if (this.lastTouchDistance) {
      const scaleChange = distance / this.lastTouchDistance;
      const newScale = this.state.scale * scaleChange;
      const constrainedScale = Math.max(
        this.state.minScale,
        Math.min(this.state.maxScale, newScale)
      );

      if (constrainedScale !== this.state.scale) {
        // Calculate center of pinch
        const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        const rect = this.canvas.getBoundingClientRect();
        const localX = centerX - rect.left;
        const localY = centerY - rect.top;

        this.zoomToPoint(localX, localY, constrainedScale);
      }
    }

    this.lastTouchDistance = distance;
  }

  /**
   * Handle wheel zoom
   */
  handleWheel(e) {
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      e.preventDefault();
      return;
    }

    // Only handle trackpad pinch gestures
    const isTrackpad = Math.abs(e.deltaY) < 50 && (e.ctrlKey || e.metaKey);
    if (!isTrackpad) return;

    e.preventDefault();

    const rect = this.canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const sensitivity = CONFIG.ZOOM_SENSITIVITY.TRACKPAD;
    const delta = e.deltaY > 0 ? 1 - sensitivity : 1 + sensitivity;
    const newScale = this.state.scale * delta;
    const constrainedScale = Math.max(this.state.minScale, Math.min(this.state.maxScale, newScale));

    this.zoomToPoint(mouseX, mouseY, constrainedScale);
  }

  /**
   * Zoom to a specific point
   */
  zoomToPoint(x, y, newScale) {
    const scaleChange = newScale / this.state.scale;

    this.state.translateX = x - (x - this.state.translateX) * scaleChange;
    this.state.translateY = y - (y - this.state.translateY) * scaleChange;
    this.state.scale = newScale;

    this.constrainTranslation();
    this.updateZoomButtons();
    this.state.needsRedraw = true;
  }

  /**
   * Constrain translation within boundaries
   */
  constrainTranslation() {
    const boundaries = this.calculateBoundaries();
    this.state.translateX = Math.max(
      boundaries.minX,
      Math.min(boundaries.maxX, this.state.translateX)
    );
    this.state.translateY = Math.max(
      boundaries.minY,
      Math.min(boundaries.maxY, this.state.translateY)
    );
  }

  /**
   * Setup zoom control buttons
   */
  setupZoomControls() {
    if (this.zoomInBtn) {
      this.zoomInBtn.addEventListener('click', () => this.zoomIn());
    }
    if (this.zoomOutBtn) {
      this.zoomOutBtn.addEventListener('click', () => this.zoomOut());
    }
    if (this.fullscreenBtn) {
      this.fullscreenBtn.addEventListener('click', () => this.toggleFullscreen());
    }
  }

  /**
   * Zoom in
   */
  zoomIn() {
    const isMobile = window.innerWidth <= 768;
    if (isMobile) return;

    const newScale = Math.min(this.state.maxScale, this.state.scale * CONFIG.ZOOM_STEP);
    const centerX = this.state.containerWidth / 2;
    const centerY = this.state.containerHeight / 2;
    this.zoomToPoint(centerX, centerY, newScale);
  }

  /**
   * Zoom out
   */
  zoomOut() {
    const isMobile = window.innerWidth <= 768;
    if (isMobile) return;

    const newScale = Math.max(this.state.minScale, this.state.scale / CONFIG.ZOOM_STEP);
    const centerX = this.state.containerWidth / 2;
    const centerY = this.state.containerHeight / 2;
    this.zoomToPoint(centerX, centerY, newScale);
  }

  /**
   * Update zoom button states
   */
  updateZoomButtons() {
    const isMobile = window.innerWidth <= 768;

    if (this.zoomInBtn) {
      this.zoomInBtn.disabled = isMobile || this.state.scale >= this.state.maxScale;
    }
    if (this.zoomOutBtn) {
      this.zoomOutBtn.disabled = isMobile || this.state.scale <= this.state.minScale;
    }
  }

  /**
   * Toggle fullscreen
   */
  toggleFullscreen() {
    if (!document.fullscreenElement) {
      this.enterFullscreen();
    } else {
      this.exitFullscreen();
    }
  }

  /**
   * Enter fullscreen
   */
  enterFullscreen() {
    if (this.mapContainer.requestFullscreen) {
      this.mapContainer.requestFullscreen().catch((err) => {
        console.error('Error enabling fullscreen:', err);
      });
    }
  }

  /**
   * Exit fullscreen
   */
  exitFullscreen() {
    if (document.exitFullscreen) {
      document.exitFullscreen().catch((err) => {
        console.error('Error exiting fullscreen:', err);
      });
    }
  }

  /**
   * Handle fullscreen change
   */
  handleFullscreenChange() {
    setTimeout(() => {
      this.updateCanvasSize();
      this.updateDimensions();
      this.state.needsRedraw = true;
    }, 100);
  }

  /**
   * Update fullscreen button icon
   */
  updateFullscreenButton() {
    if (!this.fullscreenBtn) return;

    const icon = this.fullscreenBtn.querySelector('i');
    if (icon) {
      if (this.state.isFullscreen) {
        icon.className = 'fa-solid fa-compress';
        this.fullscreenBtn.title = 'Exit Fullscreen';
      } else {
        icon.className = 'fa-solid fa-expand';
        this.fullscreenBtn.title = 'Toggle Fullscreen';
      }
    }
  }

  /**
   * Tooltip and mobile overlay event setup
   */
  setupTooltipEvents() {
    // Desktop tooltip events
    if (this.tooltip) {
      this.tooltip.addEventListener('mouseenter', () => {
        this.state.isHoveringTooltip = true;
        if (this.tooltipHideTimeout) {
          clearTimeout(this.tooltipHideTimeout);
          this.tooltipHideTimeout = null;
        }
      });

      this.tooltip.addEventListener('mouseleave', () => {
        this.state.isHoveringTooltip = false;
        this.scheduleHideTooltip();
      });
    }

    // Mobile overlay events
    if (this.mobileOverlay && this.mobileOverlayClose) {
      // Close button - use touchstart to avoid conflicts with canvas touch events
      this.mobileOverlayClose.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.hideMobileOverlay();
      });
      this.mobileOverlayClose.addEventListener('touchend', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.hideMobileOverlay();
      });

      // Close when clicking backdrop - use touchstart to avoid conflicts
      const backdrop = this.mobileOverlay.querySelector('.mobile-overlay-backdrop');
      if (backdrop) {
        backdrop.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.hideMobileOverlay();
        });
        backdrop.addEventListener('touchend', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.hideMobileOverlay();
        });
      }

      // Prevent clicks inside content from closing overlay
      const content = this.mobileOverlay.querySelector('.mobile-overlay-content');
      if (content) {
        content.addEventListener('click', (e) => {
          e.stopPropagation();
        });
        content.addEventListener('touchend', (e) => {
          e.stopPropagation();
        });
      }
    }
  }

  /**
   * Show tooltip
   */
  showTooltip(clientX, clientY, polygon) {
    if (this.tooltipHideTimeout) {
      clearTimeout(this.tooltipHideTimeout);
      this.tooltipHideTimeout = null;
    }

    if (!polygon.propertyInfo) {
      console.warn('No property data for:', polygon.objectCode);
      return;
    }

    this.updateTooltipContent(polygon.propertyInfo);
    this.updateTooltipPosition(clientX, clientY, polygon);
    this.tooltip.classList.remove('hidden');
  }

  /**
   * Update tooltip position with dynamic positioning based on available space
   */
  updateTooltipPosition(clientX, clientY, polygon) {
    // Get center of polygon in screen coordinates
    const polygonCenter = this.getPolygonScreenCenter(polygon);

    const tooltipWidth = CONFIG.TOOLTIP_DIMENSIONS.WIDTH;
    const tooltipHeight = this.tooltip.offsetHeight || CONFIG.TOOLTIP_DIMENSIONS.HEIGHT;
    const spacing = 20; // Space between tooltip and polygon
    const padding = 10; // Viewport padding

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate available space in each direction
    const spaceAbove = polygonCenter.y - padding;
    const spaceBelow = viewportHeight - polygonCenter.y - padding;
    const spaceLeft = polygonCenter.x - padding;
    const spaceRight = viewportWidth - polygonCenter.x - padding;

    // Determine best position based on available space
    let position = 'top'; // default
    let x, y;

    // Check which direction has the most space
    const spaces = {
      top: spaceAbove,
      bottom: spaceBelow,
      left: spaceLeft,
      right: spaceRight,
    };

    // Find direction with most space that can fit the tooltip
    const canFitTop = spaceAbove >= tooltipHeight + spacing;
    const canFitBottom = spaceBelow >= tooltipHeight + spacing;
    const canFitLeft = spaceLeft >= tooltipWidth + spacing;
    const canFitRight = spaceRight >= tooltipWidth + spacing;

    // Prioritize vertical positioning if there's enough space
    if (canFitTop && spaceAbove >= spaceBelow) {
      position = 'top';
      x = polygonCenter.x - tooltipWidth / 2;
      y = polygonCenter.y - tooltipHeight - spacing;
    } else if (canFitBottom) {
      position = 'bottom';
      x = polygonCenter.x - tooltipWidth / 2;
      y = polygonCenter.y + spacing;
    } else if (canFitLeft && spaceLeft >= spaceRight) {
      position = 'left';
      x = polygonCenter.x - tooltipWidth - spacing;
      y = polygonCenter.y - tooltipHeight / 2;
    } else if (canFitRight) {
      position = 'right';
      x = polygonCenter.x + spacing;
      y = polygonCenter.y - tooltipHeight / 2;
    } else {
      // Fallback: use top but adjust to fit viewport
      position = 'top';
      x = polygonCenter.x - tooltipWidth / 2;
      y = polygonCenter.y - tooltipHeight - spacing;
    }

    x = Math.max(padding, Math.min(viewportWidth - tooltipWidth - padding, x));
    y = Math.max(padding, Math.min(viewportHeight - tooltipHeight - padding, y));

    // Apply position (keep transform empty to allow CSS transitions)
    this.tooltip.style.position = 'fixed';
    this.tooltip.style.left = `${x}px`;
    this.tooltip.style.top = `${y}px`;
    this.tooltip.style.transform = '';

    // Update triangle position and direction (pass card position relative to polygon)
    this.updateTooltipTriangle(position, polygonCenter, x, y, tooltipWidth, tooltipHeight);
  }

  /**
   * Update tooltip triangle position and direction
   */
  updateTooltipTriangle(position, polygonCenter, tooltipX, tooltipY, tooltipWidth, tooltipHeight) {
    const triangle = this.tooltip.querySelector('.tooltip-triangle');
    if (!triangle) return;

    const triangleDiv = triangle.querySelector('div');
    const triangleSize = 8; // matches CSS borders

    // Reset triangle styles - use 'auto' to override Tailwind classes (bottom-0, left-1/2, etc.)
    triangle.style.top = 'auto';
    triangle.style.bottom = 'auto';
    triangle.style.left = 'auto';
    triangle.style.right = 'auto';
    triangle.style.transform = 'none';

    if (triangleDiv) {
      triangleDiv.className = 'w-0 h-0';
    }

    // Calculate triangle position relative to tooltip
    // offsetX/Y = where the polygon center is relative to tooltip's top-left corner
    const offsetX = polygonCenter.x - tooltipX;
    const offsetY = polygonCenter.y - tooltipY;

    // Clamp offsets so arrow stays within tooltip bounds
    const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
    const clampedX = clamp(offsetX, triangleSize + 4, tooltipWidth - triangleSize - 4);
    const clampedY = clamp(offsetY, triangleSize + 4, tooltipHeight - triangleSize - 4);

    // Position triangle based on side (top/bottom/left/right of tooltip)
    switch (position) {
      case 'top':
        // Card is ABOVE polygon: arrow at bottom of card, pointing down
        triangle.style.bottom = `${triangleSize}px`;
        triangle.style.left = `${clampedX}px`;
        triangle.style.transform = 'translateX(-50%) translateY(100%)';
        if (triangleDiv) {
          triangleDiv.className =
            'w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-white';
        }
        break;

      case 'bottom':
        // Card is BELOW polygon: arrow at top of card, pointing up
        triangle.style.top = `-${triangleSize}px`;
        triangle.style.left = `${clampedX}px`;
        triangle.style.transform = 'translateX(-50%) translateY(-100%)';
        if (triangleDiv) {
          triangleDiv.className =
            'w-0 h-0 border-l-8 border-r-8 border-b-8 border-transparent border-b-white';
        }
        break;

      case 'left':
        // Card is LEFT of polygon: arrow at right side of card, pointing right
        // Triangle is 8px wide (border-l-8), 16px tall (8+8 from top/bottom borders)
        triangle.style.top = `${clampedY}px`;
        triangle.style.bottom = 'auto';
        triangle.style.left = '100%'; // Position at right edge of tooltip
        triangle.style.right = 'auto';
        // Manual offset: +4px right, -20px up for better alignment
        triangle.style.transform = 'translateX(4px) translateY(-20px)';
        if (triangleDiv) {
          triangleDiv.className =
            'w-0 h-0 border-t-8 border-b-8 border-l-8 border-transparent border-l-white';
        }
        break;

      case 'right':
        // Card is RIGHT of polygon: arrow at left side of card, pointing left
        // Triangle is 8px wide (border-r-8), 16px tall (8+8 from top/bottom borders)
        triangle.style.top = `${clampedY}px`;
        triangle.style.bottom = 'auto';
        triangle.style.right = '100%'; // Position at left edge of tooltip
        triangle.style.left = 'auto';
        // Manual offset: +4px right, -20px up for better alignment
        triangle.style.transform = 'translateX(4px) translateY(-20px)';
        if (triangleDiv) {
          triangleDiv.className =
            'w-0 h-0 border-t-8 border-b-8 border-r-8 border-transparent border-r-white';
        }
        break;
    }
  }

  /**
   * Get polygon center in screen coordinates
   */
  getPolygonScreenCenter(polygon) {
    // Calculate average of all points
    let sumX = 0,
      sumY = 0;
    polygon.points.forEach((point) => {
      sumX += point.x * this.state.imageWidth;
      sumY += point.y * this.state.imageHeight;
    });

    const centerX = sumX / polygon.points.length;
    const centerY = sumY / polygon.points.length;

    // Convert to screen coordinates
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: rect.left + this.state.translateX + centerX * this.state.scale,
      y: rect.top + this.state.translateY + centerY * this.state.scale,
    };
  }

  /**
   * Update tooltip content
   */
  updateTooltipContent(propertyData) {
    if (this.tooltipImage) {
      const imageUrl = propertyData.image;

      // Always ensure transition is set
      this.tooltipImage.style.transition = 'opacity 0.2s ease-in-out';

      // Preload image before setting src to prevent flickering
      if (imageUrl) {
        // If image URL is different or src is empty (from previous hide), preload
        if (imageUrl !== this.tooltipImage.src && imageUrl !== this.requestedImageUrl) {
          // Cancel any previous image load that's still in progress
          if (this.currentImageLoad) {
            this.currentImageLoad.onload = null;
            this.currentImageLoad.onerror = null;
            this.currentImageLoad.src = ''; // Cancel the load
            this.currentImageLoad = null;
          }

          // Track the URL we're requesting
          this.requestedImageUrl = imageUrl;

          // Hide image during load
          this.tooltipImage.style.opacity = '0';

          // Preload the new image
          const img = new Image();
          this.currentImageLoad = img;

          img.onload = () => {
            // Only apply if this is still the requested URL (handles rapid hover changes)
            if (this.requestedImageUrl === imageUrl && this.currentImageLoad === img) {
              // Image loaded, set src and fade in
              this.tooltipImage.src = imageUrl;
              this.tooltipImage.alt = propertyData.title || '';
              requestAnimationFrame(() => {
                if (this.requestedImageUrl === imageUrl) {
                  this.tooltipImage.style.opacity = '1';
                }
              });
            }
            this.currentImageLoad = null;
          };

          img.onerror = () => {
            // Only apply if this is still the requested URL
            if (this.requestedImageUrl === imageUrl && this.currentImageLoad === img) {
              // Image failed, show placeholder
              this.tooltipImage.src = '';
              this.tooltipImage.style.opacity = '1';
            }
            this.currentImageLoad = null;
          };

          img.src = imageUrl;
        }
        // If image URL is the same as currently displayed or already loading, just ensure it's visible
        else if (imageUrl === this.tooltipImage.src) {
          this.tooltipImage.alt = propertyData.title || '';
          this.tooltipImage.style.opacity = '1';
        }
      } else {
        // No image URL, clear and ensure visible
        // Cancel any in-progress load
        if (this.currentImageLoad) {
          this.currentImageLoad.onload = null;
          this.currentImageLoad.onerror = null;
          this.currentImageLoad.src = '';
          this.currentImageLoad = null;
        }
        this.requestedImageUrl = null;
        this.tooltipImage.src = '';
        this.tooltipImage.alt = propertyData.title || '';
        this.tooltipImage.style.opacity = '1';
      }
    }

    if (this.tooltipTitle) {
      this.tooltipTitle.textContent = propertyData.title || 'Woning';
    }

    if (this.tooltipLocation) {
      const locationParts = [];
      if (propertyData.park) locationParts.push(propertyData.park);
      if (propertyData.location) locationParts.push(propertyData.location);
      this.tooltipLocation.textContent = locationParts.join(', ') || '';
    }

    if (this.tooltipDescription) {
      this.tooltipDescription.textContent = propertyData.description || '';
    }

    if (this.tooltipPrice) {
      this.tooltipPrice.textContent = propertyData.price || '';
    }

    if (this.tooltipButton) {
      this.tooltipButton.style.display = '';
      this.tooltipButton.onclick = () => {
        if (propertyData.permalink) {
          window.location.href = propertyData.permalink;
        }
      };
    }
  }

  /**
   * Schedule tooltip hide
   */
  scheduleHideTooltip() {
    this.tooltipHideTimeout = setTimeout(() => {
      if (!this.state.isHoveringTooltip) {
        this.hideTooltip();
      }
    }, 200);
  }

  /**
   * Hide tooltip
   */
  hideTooltip() {
    // Cancel any in-progress image loads
    if (this.currentImageLoad) {
      this.currentImageLoad.onload = null;
      this.currentImageLoad.onerror = null;
      this.currentImageLoad.src = '';
      this.currentImageLoad = null;
    }
    this.requestedImageUrl = null;

    if (this.tooltip) {
      // Add hidden class first to trigger CSS transitions
      this.tooltip.classList.add('hidden');

      // Clear image after tooltip fade-out completes
      // Use transparent 1px data URL to maintain image dimensions and prevent layout shift
      setTimeout(() => {
        if (this.tooltipImage) {
          // Transparent 1px GIF maintains container dimensions
          this.tooltipImage.src =
            'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
          this.tooltipImage.style.opacity = '0';
        }
      }, 200); // Slightly longer than CSS transition (150ms) to ensure tooltip is hidden
    }

    if (this.tooltipHideTimeout) {
      clearTimeout(this.tooltipHideTimeout);
      this.tooltipHideTimeout = null;
    }
  }

  /**
   * Show mobile overlay with property info
   */
  showMobileOverlay(polygon) {
    if (!this.mobileOverlay || !polygon.propertyInfo) {
      console.warn('Mobile overlay or property data not available');
      return;
    }

    // Update content
    this.updateMobileOverlayContent(polygon.propertyInfo);

    // Show overlay with animation
    this.mobileOverlay.classList.remove('hidden');

    // Set flag to prevent immediate hiding
    this.state.overlayJustShown = true;
    setTimeout(() => {
      this.state.overlayJustShown = false;
    }, 300); // Allow time for overlay animation to complete

    // Prevent body scroll when overlay is open
    document.body.style.overflow = 'hidden';
  }

  /**
   * Hide mobile overlay
   */
  hideMobileOverlay() {
    if (!this.mobileOverlay) return;

    // Prevent hiding if overlay was just shown (prevents touch event propagation issue)
    if (this.state.overlayJustShown) {
      return;
    }

    // Cancel any in-progress image loads
    if (this.currentMobileImageLoad) {
      this.currentMobileImageLoad.onload = null;
      this.currentMobileImageLoad.onerror = null;
      this.currentMobileImageLoad.src = '';
      this.currentMobileImageLoad = null;
    }
    this.requestedMobileImageUrl = null;

    this.mobileOverlay.classList.add('hidden');

    // Restore body scroll
    document.body.style.overflow = '';
  }

  /**
   * Update mobile overlay content with property data
   */
  updateMobileOverlayContent(propertyData) {
    if (this.mobileOverlayImage) {
      const imageUrl = propertyData.image;

      // Preload image before setting src to prevent flickering
      if (imageUrl) {
        if (imageUrl !== this.mobileOverlayImage.src && imageUrl !== this.requestedMobileImageUrl) {
          // Cancel any previous image load that's still in progress
          if (this.currentMobileImageLoad) {
            this.currentMobileImageLoad.onload = null;
            this.currentMobileImageLoad.onerror = null;
            this.currentMobileImageLoad.src = '';
            this.currentMobileImageLoad = null;
          }

          // Track the URL we're requesting
          this.requestedMobileImageUrl = imageUrl;

          this.mobileOverlayImage.style.opacity = '0';
          this.mobileOverlayImage.style.transition = 'opacity 0.2s ease-in-out';

          const img = new Image();
          this.currentMobileImageLoad = img;

          img.onload = () => {
            // Only apply if this is still the requested URL
            if (this.requestedMobileImageUrl === imageUrl && this.currentMobileImageLoad === img) {
              this.mobileOverlayImage.src = imageUrl;
              this.mobileOverlayImage.alt = propertyData.title || '';
              requestAnimationFrame(() => {
                if (this.requestedMobileImageUrl === imageUrl) {
                  this.mobileOverlayImage.style.opacity = '1';
                }
              });
            }
            this.currentMobileImageLoad = null;
          };

          img.onerror = () => {
            if (this.requestedMobileImageUrl === imageUrl && this.currentMobileImageLoad === img) {
              this.mobileOverlayImage.style.opacity = '1';
            }
            this.currentMobileImageLoad = null;
          };

          img.src = imageUrl;
        } else if (imageUrl === this.mobileOverlayImage.src) {
          this.mobileOverlayImage.alt = propertyData.title || '';
          this.mobileOverlayImage.style.opacity = '1';
        }
      } else {
        // Cancel any in-progress load
        if (this.currentMobileImageLoad) {
          this.currentMobileImageLoad.onload = null;
          this.currentMobileImageLoad.onerror = null;
          this.currentMobileImageLoad.src = '';
          this.currentMobileImageLoad = null;
        }
        this.requestedMobileImageUrl = null;
        this.mobileOverlayImage.src = '';
        this.mobileOverlayImage.alt = propertyData.title || '';
        this.mobileOverlayImage.style.opacity = '1';
      }
    }

    if (this.mobileOverlayTitle) {
      this.mobileOverlayTitle.textContent = propertyData.title || 'Woning';
    }

    if (this.mobileOverlayLocation) {
      const locationParts = [];
      if (propertyData.park) locationParts.push(propertyData.park);
      if (propertyData.location) locationParts.push(propertyData.location);
      this.mobileOverlayLocation.textContent = locationParts.join(', ') || '';
    }

    if (this.mobileOverlayDescription) {
      this.mobileOverlayDescription.textContent = propertyData.description || '';
    }

    if (this.mobileOverlayPrice) {
      this.mobileOverlayPrice.textContent = propertyData.price || '';
    }

    if (this.mobileOverlayButton) {
      this.mobileOverlayButton.onclick = () => {
        if (propertyData.permalink) {
          window.location.href = propertyData.permalink;
        }
      };
    }
  }

  /**
   * Adjust color opacity
   */
  adjustOpacity(color, newOpacity) {
    if (color.startsWith('rgba')) {
      return color.replace(/[\d\.]+\)$/g, `${newOpacity})`);
    }
    if (color.startsWith('rgb')) {
      return color.replace('rgb', 'rgba').replace(')', `, ${newOpacity})`);
    }
    return color;
  }

  /**
   * Cleanup
   */
  destroy() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }

    // Restore body scroll if overlay was open
    if (this.mobileOverlay && !this.mobileOverlay.classList.contains('hidden')) {
      document.body.style.overflow = '';
    }
  }
}

/**
 * Initialize interactive map (global function)
 */
window.initInteractiveMap = function (blockId, containerId, mapData, propertyData) {
  return new InteractiveMap(blockId, containerId, mapData, propertyData);
};